import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Shirt,
  Search,
  User,
  Building2,
  ChevronRight,
  ArrowLeft,
  Minus,
  Plus,
  CheckCircle,
  AlertTriangle,
  Camera,
  Trash2,
  Loader2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { fetchClientes } from '@/services/clientes.service';
import { getClientAssignedGarments, getClientGarments } from '@/pages/parametrizacion/clientGarments/Services/clientGarments.services';
import { createEntregaCliente } from '@/services/entregasClientes.service';
import { uploadDocumentFlow } from '@/services/documentsService';
import { getPrinterInstance } from '@/utils/bluetoothPrinter';
import { compressImage } from '@/utils/imageCompression';

/* ──────────────────────────────────────────────────────────────
   Componente principal
────────────────────────────────────────────────────────────── */
const RecoleccionRopaSucia = ({ idLavanderia, lavanderiaNombre: lavNombreProp, onRefresh, onNavigateToRecolectadas }) => {
  const { toast } = useToast();
  const { user } = useAuth();

  const [step, setStep] = useState('buscar');
  const [clientes, setClientes] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [isLoadingClientes, setIsLoadingClientes] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [isLoadingPrendas, setIsLoadingPrendas] = useState(false);
  const [prendasSeleccionadas, setPrendasSeleccionadas] = useState([]);
  const [recibeNombre, setRecibeNombre] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [tipoRecoleccion, setTipoRecoleccion] = useState('ordinaria');
  const [isCreating, setIsCreating] = useState(false);
  const [evidenciaFiles, setEvidenciaFiles] = useState([]);
  const [evidenciaPreviews, setEvidenciaPreviews] = useState([]);
  const [uploadingEvidencia, setUploadingEvidencia] = useState(false);
  const printer = getPrinterInstance();

  const isConductor = String(user?.role ?? user?.rol ?? '').toLowerCase() === 'conductor';

  // IDs de clientes asignados al conductor (soporta array de strings o de objetos)
  const clientesAsignadosIds = React.useMemo(() => {
    const arr = user?.raw?.clientesAsignados;
    if (!Array.isArray(arr)) return null;
    // c.id = ID del cliente (referencia), c._id = ID del subdocumento MongoDB — usar c.id primero
    return arr.map((c) => (typeof c === 'string' ? c : String(c?.id || c?._id || '')));
  }, [user]);

  useEffect(() => {
    loadClientes();
  }, []);

  useEffect(() => {
    filterClientes();
  }, [searchTerm, clientes]);

  const loadClientes = async () => {
    setIsLoadingClientes(true);
    try {
      // Para conductores enviamos sus IDs al backend para filtrar desde el origen.
      // Para todos usamos un límite alto para no truncar la lista por la paginación.
      const query = { estado: 'Activo' };
      if (isConductor && Array.isArray(clientesAsignadosIds) && clientesAsignadosIds.length > 0) {
        query.ids = clientesAsignadosIds;
      }
      const limit = isConductor && Array.isArray(clientesAsignadosIds)
        ? Math.max(clientesAsignadosIds.length, 100)
        : 500;

      const response = await fetchClientes({ ...query, limit });
      const todos = (response.state || response.success) && Array.isArray(response.data) ? response.data : [];

      // Garantía adicional en frontend: si el backend no filtra por ids, lo hacemos aquí
      if (isConductor && Array.isArray(clientesAsignadosIds)) {
        const filtrados = todos.filter((c) => clientesAsignadosIds.includes(String(c._id || c.id || '')));
        setClientes(filtrados);
        setFilteredClientes(filtrados);
      } else {
        setClientes(todos);
        setFilteredClientes(todos);
      }
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar los clientes', variant: 'destructive' });
      setClientes([]);
    } finally {
      setIsLoadingClientes(false);
    }
  };

  const filterClientes = () => {
    if (!searchTerm.trim()) {
      setFilteredClientes(clientes);
      return;
    }
    const term = searchTerm.toLowerCase();
    setFilteredClientes(
      clientes.filter(
        (c) =>
          (c.nombre || '').toLowerCase().includes(term) ||
          (c.nit || '').toLowerCase().includes(term)
      )
    );
  };

  const handleSelectCliente = async (cliente) => {
    setSelectedCliente(cliente);
    setStep('confirmar');
  };

  const handleConfirmarCliente = async () => {
    setStep('prendas');
    setIsLoadingPrendas(true);
    try {
      const [assignResponse, garmentResponse] = await Promise.all([
        getClientAssignedGarments(selectedCliente._id),
        getClientGarments({ limit: 500 }),
      ]);

      const garments = Array.isArray(assignResponse.data) ? assignResponse.data : [];

      // Mapa prendaId → familia usando la colección base client-garments
      const familiaMap = new Map();
      (garmentResponse.data || []).forEach((g) => {
        familiaMap.set(String(g._id), g.familia || null);
      });

      setPrendasSeleccionadas(
        garments.map((g) => {
          const pid = g.prendaId || g.tipoPrenda?._id || g.tipoPrenda?.id || g._id;
          return {
            prendaId: pid,
            nombre: g.tipoPrenda?.nombre || g.nombre || 'Prenda',
            peso: g.peso || 0,
            cantidad: 0,
            familia: familiaMap.get(String(pid)) || null,
          };
        })
      );
    } catch (error) {
      console.error('Error al cargar prendas del cliente:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar las prendas del cliente', variant: 'destructive' });
      setPrendasSeleccionadas([]);
    } finally {
      setIsLoadingPrendas(false);
    }
  };

  const updateCantidad = (index, value) => {
    setPrendasSeleccionadas((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], cantidad: Math.max(0, value) };
      return updated;
    });
  };

  const handleEvidenciaChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setEvidenciaFiles((prev) => [...prev, ...files]);
    setEvidenciaPreviews((prev) => [...prev, ...newPreviews]);
    e.target.value = '';
  };

  const handleRemoveEvidencia = (index) => {
    URL.revokeObjectURL(evidenciaPreviews[index]);
    setEvidenciaFiles((prev) => prev.filter((_, i) => i !== index));
    setEvidenciaPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const totalPrendas = prendasSeleccionadas.reduce((s, p) => s + p.cantidad, 0);

  // Prendas agrupadas por familia (con índice original para updateCantidad)
  const prendasGrouped = useMemo(() => {
    const groups = new Map();
    prendasSeleccionadas.forEach((prenda, idx) => {
      const key = prenda.familia?.nombre || 'Sin Familia';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({ ...prenda, _idx: idx });
    });
    return Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === 'Sin Familia') return 1;
      if (b === 'Sin Familia') return -1;
      return a.localeCompare(b);
    });
  }, [prendasSeleccionadas]);

  const handleGoToResumen = () => {
    if (totalPrendas === 0) {
      toast({ title: 'Advertencia', description: 'Selecciona al menos una prenda', variant: 'destructive' });
      return;
    }
    setStep('resumen');
  };

  const handleCrearRecoleccion = async () => {
    setIsCreating(true);
    try {
      const items = prendasSeleccionadas
        .filter((p) => p.cantidad > 0)
        .map((p) => ({
          prendaId: p.prendaId,
          nombre: p.nombre,
          peso: p.peso,
          cantidad_alistada: p.cantidad,
          cantidad_confirmada: p.cantidad,
        }));

      const clienteLav = selectedCliente?.lavanderia || {};
      const lavanderiaId = clienteLav.id || clienteLav._id || idLavanderia || '';
      const lavanderiaNombre = clienteLav.nombre || lavNombreProp || '';
      const conductorId = user?.id || user?._id || user?.raw?._id || '';
      const conductorNombre = ((user?.name || user?.raw?.nombre || '') + ' ' + (user?.raw?.apellido || '')).trim();

      if (!lavanderiaId || !lavanderiaNombre) {
        toast({
          title: 'Error',
          description: `Datos de lavandería incompletos (id: ${lavanderiaId || 'vacío'}, nombre: ${lavanderiaNombre || 'vacío'}). Contacte al administrador.`,
          variant: 'destructive',
        });
        setIsCreating(false);
        return;
      }

      let evidenciaIds = [];
      if (evidenciaFiles.length > 0) {
        setUploadingEvidencia(true);
        try {
          const compressedFiles = await Promise.all(
            evidenciaFiles.map((file) => compressImage(file))
          );
          const uploadPromises = compressedFiles.map((file) =>
            uploadDocumentFlow({
              ownerType: 'laundry_center',
              ownerId: lavanderiaId,
              docType: 'evidencia_recoleccion',
              file,
            })
          );
          const uploadResults = await Promise.all(uploadPromises);
          evidenciaIds = uploadResults.map((result) => result.id);
        } catch (uploadErr) {
          console.error('Error subiendo evidencias:', uploadErr);
          toast({ title: 'Error', description: 'No se pudieron subir las evidencias', variant: 'destructive' });
          setUploadingEvidencia(false);
          setIsCreating(false);
          return;
        } finally {
          setUploadingEvidencia(false);
        }
      }

      const data = {
        tipo: 'Recoleccion',
        estado: 'Entregado',
        lavanderia: { id: lavanderiaId, nombre: lavanderiaNombre },
        cliente: { id: selectedCliente._id, nombre: selectedCliente.nombre, nit: selectedCliente.nit || '' },
        items,
        conductor: { id: conductorId, nombre: conductorNombre || 'Conductor' },
        recibe: { nombre: recibeNombre || 'N/A' },
        fecha_recoleccion: new Date().toISOString(),
        tipoRecoleccion,
        observaciones,
        evidencias: evidenciaIds,
      };

      const response = await createEntregaCliente(data);

      if (response.state || response.data) {
        const recoleccionData = response.data;

        // Auto-imprimir por Bluetooth
        if (printer.isBluetoothAvailable()) {
          try {
            if (!printer.isConnected()) {
              toast({ title: 'Conectando impresora...', description: 'Selecciona tu impresora Bluetooth' });
              await printer.connect();
            }
            // Enriquecer items del response con la familia (disponible en prendasSeleccionadas)
            const familiaByPrendaId = new Map(
              prendasSeleccionadas.map((p) => [String(p.prendaId), p.familia])
            );
            const dataToPrint = {
              ...recoleccionData,
              items: (recoleccionData.items || []).map((item) => ({
                ...item,
                familia: familiaByPrendaId.get(String(item.prendaId)) || null,
              })),
            };
            await printer.printRecoleccion(dataToPrint);
            toast({ title: 'Recolección creada e impresa', description: recoleccionData?.consecutivo });
          } catch (printErr) {
            const raw = (printErr?.message || '').toLowerCase();
            const msg = raw.includes('user cancelled') || raw.includes('user denied')
              ? 'Impresión cancelada. Puedes reimprimir desde "Recolectadas".'
              : 'No se pudo imprimir. Puedes reimprimir desde "Recolectadas".';
            toast({ title: 'Recolección creada — ' + recoleccionData?.consecutivo, description: msg, variant: 'destructive' });
          }
        } else {
          toast({ title: 'Recolección creada', description: recoleccionData?.consecutivo });
        }

        onRefresh?.();
        onNavigateToRecolectadas?.();
      } else {
        throw new Error(response.message || 'Error al crear recoleccion');
      }
    } catch (error) {
      console.error('Error al crear recoleccion:', error);
      toast({ title: 'Error', description: error.message || 'No se pudo crear la recoleccion', variant: 'destructive' });
    } finally {
      setIsCreating(false);
    }
  };


  const handleVolver = () => {
    if (step === 'resumen') {
      setStep('prendas');
    } else if (step === 'prendas') {
      setStep('confirmar');
    } else if (step === 'confirmar') {
      setStep('buscar');
      setSelectedCliente(null);
      evidenciaPreviews.forEach((url) => URL.revokeObjectURL(url));
      setEvidenciaFiles([]);
      setEvidenciaPreviews([]);
    }
  };

  // ── STEP: Buscar Cliente ────────────────────────────────────
  if (step === 'buscar') {
    if (isLoadingClientes) {
      return (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto" />
          <p className="text-sm text-gray-500 mt-4">Cargando clientes...</p>
        </div>
      );
    }

    // Conductor sin clientes asignados
    if (isConductor && Array.isArray(clientesAsignadosIds) && clientesAsignadosIds.length === 0) {
      return (
        <Card>
          <CardContent className="p-12 text-center">
            <User className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Sin clientes asignados</h3>
            <p className="text-sm text-gray-500">
              No tienes clientes asignados. Solicita al administrador que te asigne clientes desde la parametrización de usuarios.
            </p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Buscar cliente por nombre o NIT..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 text-sm h-10"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {filteredClientes.length} de {clientes.length} cliente(s)
            </p>
          </CardContent>
        </Card>

        {filteredClientes.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <User className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                {searchTerm ? 'No se encontraron clientes' : 'No hay clientes disponibles'}
              </h3>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-2">
            {filteredClientes.map((cliente) => (
              <motion.div
                key={cliente._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card
                  className="hover:shadow-md hover:border-purple-300 transition-all cursor-pointer active:scale-[0.98]"
                  onClick={() => handleSelectCliente(cliente)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                        <User className="h-5 w-5 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 text-sm truncate">{cliente.nombre}</h3>
                        {cliente.nit && (
                          <p className="text-xs text-gray-500">NIT: {cliente.nit}</p>
                        )}
                        {cliente.lavanderia?.nombre && (
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {cliente.lavanderia.nombre}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── STEP: Confirmar Cliente ────────────────────────────────
  if (step === 'confirmar') {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={handleVolver} className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex-1">
                <h3 className="font-semibold text-sm">Confirmar Cliente</h3>
                <p className="text-xs text-gray-500">Verifica que sea el cliente correcto</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-orange-200 bg-orange-50/30">
          <CardContent className="p-6 text-center space-y-4">
            <div className="flex justify-center">
              <div className="p-4 bg-orange-100 rounded-full">
                <AlertTriangle className="h-10 w-10 text-orange-500" />
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-gray-600">Estás a punto de recolectar ropa sucia de:</p>
              <h2 className="text-xl font-bold text-gray-900">{selectedCliente?.nombre}</h2>
              {selectedCliente?.nit && (
                <p className="text-sm text-gray-500">NIT: {selectedCliente.nit}</p>
              )}
              {selectedCliente?.lavanderia?.nombre && (
                <p className="text-sm text-gray-500 flex items-center justify-center gap-1">
                  <Building2 className="h-3 w-3" />
                  {selectedCliente.lavanderia.nombre}
                </p>
              )}
            </div>
            <p className="text-xs text-gray-400">
              ¿Es este el cliente correcto?
            </p>
          </CardContent>
        </Card>

        <div className="sticky bottom-0 bg-white border-t p-3 space-y-2">
          <Button
            onClick={handleConfirmarCliente}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white gap-2"
          >
            <CheckCircle className="h-4 w-4" />
            Sí, continuar
          </Button>
          <Button
            variant="outline"
            onClick={handleVolver}
            className="w-full gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            No, elegir otro cliente
          </Button>
        </div>
      </div>
    );
  }

  // ── STEP: Seleccionar prendas ───────────────────────────────
  if (step === 'prendas') {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={handleVolver} className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex-1">
                <h3 className="font-semibold text-sm">{selectedCliente?.nombre}</h3>
                <p className="text-xs text-gray-500">Selecciona las prendas sucias a recoger</p>
              </div>
              <Badge className="bg-purple-100 text-purple-800">
                {totalPrendas} prendas
              </Badge>
            </div>
          </CardContent>
        </Card>

        {isLoadingPrendas ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto" />
            <p className="text-sm text-gray-500 mt-4">Cargando prendas...</p>
          </div>
        ) : prendasSeleccionadas.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Shirt className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No hay prendas asignadas</h3>
              <p className="text-sm text-gray-500">
                Este cliente no tiene prendas asignadas. Asigna prendas desde la configuración del cliente.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="space-y-4">
              {prendasGrouped.map(([familiaName, items]) => {
                const totalGrupo = items.reduce((s, p) => s + p.cantidad, 0);
                return (
                  <div key={familiaName}>
                    {/* Cabecera de familia */}
                    <div className="flex items-center justify-between px-1 mb-2">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full bg-purple-500" />
                        <span className="text-xs font-bold text-purple-700 uppercase tracking-wide">
                          {familiaName}
                        </span>
                      </div>
                      {totalGrupo > 0 && (
                        <Badge className="bg-purple-100 text-purple-800 text-[10px]">
                          {totalGrupo} uds
                        </Badge>
                      )}
                    </div>
                    <div className="grid gap-2">
                      {items.map((prenda) => (
                        <Card
                          key={prenda._idx}
                          className={prenda.cantidad > 0 ? 'border-purple-300 bg-purple-50/50' : ''}
                        >
                          <CardContent className="p-3">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{prenda.nombre}</p>
                                {prenda.peso > 0 && (
                                  <p className="text-xs text-gray-500">{prenda.peso}g</p>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => updateCantidad(prenda._idx, prenda.cantidad - 1)}
                                >
                                  <Minus className="h-3 w-3" />
                                </Button>
                                <Input
                                  type="number"
                                  className="w-16 h-8 text-center text-sm"
                                  value={prenda.cantidad || ''}
                                  onChange={(e) => updateCantidad(prenda._idx, parseInt(e.target.value) || 0)}
                                  min={0}
                                />
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => updateCantidad(prenda._idx, prenda.cantidad + 1)}
                                >
                                  <Plus className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="sticky bottom-0 bg-white border-t p-3">
              <Button
                onClick={handleGoToResumen}
                disabled={totalPrendas === 0}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Continuar ({totalPrendas} prendas)
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  // ── STEP: Resumen y confirmación ────────────────────────────
  if (step === 'resumen') {
    const itemsConCantidad = prendasSeleccionadas.filter((p) => p.cantidad > 0);

    // Agrupar resumen por familia
    const resumenPorFamilia = (() => {
      const groups = new Map();
      itemsConCantidad.forEach((p) => {
        const key = p.familia?.nombre || 'Sin Familia';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(p);
      });
      return Array.from(groups.entries()).sort(([a], [b]) => {
        if (a === 'Sin Familia') return 1;
        if (b === 'Sin Familia') return -1;
        return a.localeCompare(b);
      });
    })();

    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={handleVolver} className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h3 className="font-semibold text-sm">Resumen de Recolección</h3>
                <p className="text-xs text-gray-500">{selectedCliente?.nombre}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <h4 className="font-semibold text-sm">Prendas a recolectar</h4>
            {resumenPorFamilia.map(([familiaName, items]) => (
              <div key={familiaName}>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="h-2 w-2 rounded-full bg-purple-500" />
                  <span className="text-[11px] font-bold text-purple-700 uppercase tracking-wide">
                    {familiaName}
                  </span>
                </div>
                <div className="space-y-1 pl-4">
                  {items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                      <span className="text-gray-800">{item.nombre}</span>
                      <Badge variant="outline">{item.cantidad} uds</Badge>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between text-sm font-bold pt-2 border-t">
              <span>Total</span>
              <span>{totalPrendas} prendas</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <div>
              <Label htmlFor="tipo-rec">Tipo de recolección</Label>
              <div className="flex gap-2 mt-1">
                {['ordinaria', 'extra', 'dominical'].map((tipo) => (
                  <button
                    key={tipo}
                    type="button"
                    onClick={() => setTipoRecoleccion(tipo)}
                    className={`flex-1 py-2 rounded-md border text-sm font-medium capitalize transition-colors ${tipoRecoleccion === tipo
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'
                      }`}
                  >
                    {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="recibe-rec">Quien entrega la ropa (Cliente)</Label>
              <Input
                id="recibe-rec"
                placeholder="Nombre de quien entrega..."
                value={recibeNombre}
                onChange={(e) => setRecibeNombre(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="obs-rec">Observaciones (opcional)</Label>
              <Input
                id="obs-rec"
                placeholder="Observaciones..."
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
              />
            </div>
            <div>
              <Label>Evidencia fotográfica (opcional)</Label>
              <div className="mt-2 space-y-2">
                {evidenciaPreviews.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {evidenciaPreviews.map((preview, idx) => (
                      <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                        <img
                          src={preview}
                          alt={`Evidencia ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveEvidencia(idx)}
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-bl-lg p-1 hover:bg-red-600"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    id="evidencia-rec"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleEvidenciaChange}
                    className="hidden"
                    disabled={uploadingEvidencia}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('evidencia-rec').click()}
                    disabled={uploadingEvidencia}
                    className="gap-2"
                  >
                    {uploadingEvidencia ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                    Tomar foto
                  </Button>
                  <input
                    id="evidencia-gallery"
                    type="file"
                    accept="image/*"
                    onChange={handleEvidenciaChange}
                    className="hidden"
                    disabled={uploadingEvidencia}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('evidencia-gallery').click()}
                    disabled={uploadingEvidencia}
                    className="gap-2"
                  >
                    {uploadingEvidencia ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                    Galería
                  </Button>
                </div>
                {evidenciaPreviews.length > 0 && (
                  <p className="text-xs text-gray-500">{evidenciaPreviews.length} foto(s) adjuntada(s)</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="sticky bottom-0 bg-white border-t p-3">
          <Button
            onClick={handleCrearRecoleccion}
            disabled={isCreating || uploadingEvidencia}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white gap-2"
          >
            {isCreating || uploadingEvidencia ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                {uploadingEvidencia ? 'Subiendo evidencias...' : 'Creando...'}
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4" />
                Confirmar Recolección
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default RecoleccionRopaSucia;
